/**
 * PAY-01C: AR Payment Plan Detail
 * Route: /ar/payment-plans/:planId  (ar.plan.view)
 *
 * Tabs:
 *  1. Installment Schedule — due dates, status, actions (raise invoice, link receipt, link advance, waive)
 *  2. Reminder History — queued jobs + delivered reminders (mock)
 *  3. Audit Trail
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, CheckCircle2, Clock, AlertTriangle,
  DollarSign, Bell, FileText, History, ChevronDown, ChevronUp,
  Send, CreditCard, PiggyBank, AlertOctagon, Play, Ban, Zap,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { arPayPlanApi, arReceiptApi, formatError } from "@/services/api";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(minor, currency = "INR") {
  if (minor == null || minor === "") return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function fmtDatetime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function pct(applied, scheduled) {
  if (!scheduled || scheduled === 0) return 0;
  return Math.min(100, Math.round((applied / scheduled) * 100));
}

const INST_CFG = {
  SCHEDULED:     { label: "Scheduled",   color: "#64748B", bg: "#F1F5F9", Icon: Clock },
  DUE:           { label: "Due",         color: "#B45309", bg: "#FEF3C7", Icon: AlertTriangle },
  INVOICE_RAISED:{ label: "Inv Raised",  color: "#1D4ED8", bg: "#EFF6FF", Icon: FileText },
  PARTIALLY_PAID:{ label: "Part Paid",   color: "#7C3AED", bg: "#F5F3FF", Icon: CreditCard },
  PAID:          { label: "Paid",        color: "#166534", bg: "#F0FDF4", Icon: CheckCircle2 },
  OVERDUE:       { label: "Overdue",     color: "#DC2626", bg: "#FEF2F2", Icon: AlertOctagon },
  WAIVED:        { label: "Waived",      color: "#64748B", bg: "#F5F5F5", Icon: Ban },
  CANCELLED:     { label: "Cancelled",   color: "#9CA3AF", bg: "#F5F5F5", Icon: Ban },
};

const PLAN_CFG = {
  DRAFT:      { label: "Draft",     color: "#64748B", bg: "#F1F5F9" },
  ACTIVE:     { label: "Active",    color: "#1D4ED8", bg: "#EFF6FF" },
  COMPLETED:  { label: "Completed", color: "#166534", bg: "#F0FDF4" },
  CANCELLED:  { label: "Cancelled", color: "#991B1B", bg: "#FEF2F2" },
};

function Badge({ status, cfg }) {
  const c = cfg?.[status] || { label: status, color: "#64748B", bg: "#F1F5F9" };
  return (
    <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.color}30`,
      borderRadius: 9999, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
      {c.label || status}
    </span>
  );
}

function ProgressBar({ applied, scheduled }) {
  const p = pct(applied, scheduled);
  const color = p >= 100 ? "#166534" : p > 0 ? "#7C3AED" : "#E2E8F0";
  return (
    <div style={{ background: "#F1F5F9", borderRadius: 999, height: 6, width: "100%", overflow: "hidden" }}>
      <div style={{ width: `${p}%`, background: color, height: "100%", transition: "width 0.3s" }} />
    </div>
  );
}

// ── Modals ────────────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, onConfirm, onClose, confirmLabel = "Confirm", confirmColor = "#DC2626", field, fieldLabel }) {
  const [value, setValue] = useState("");
  const valid = !field || value.trim().length >= 3;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 12, maxWidth: 440, width: "100%", padding: 24 }}>
        <h3 style={{ fontWeight: 700, fontSize: 16, color: "#0F172A", margin: "0 0 8px" }}>{title}</h3>
        <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 16px" }}>{message}</p>
        {field && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
              {fieldLabel || "Reason"}
            </label>
            <input value={value} onChange={e => setValue(e.target.value)}
              placeholder="Enter reason (min 3 chars)"
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1",
                borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose}
            style={{ padding: "8px 18px", border: "1px solid #CBD5E1", borderRadius: 8,
              background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={() => valid && onConfirm(value)} disabled={!valid}
            style={{ padding: "8px 18px", border: "none", borderRadius: 8,
              background: valid ? confirmColor : "#CBD5E1", color: "#fff",
              fontSize: 13, fontWeight: 600, cursor: valid ? "pointer" : "default" }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkReceiptModal({ plan, inst, onClose, onDone }) {
  const [receipts, setReceipts] = useState([]);
  const [selectedId, setSelected] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    arReceiptApi.getCustomerReceipts(plan.customer_id, { status: "VERIFIED,PARTIALLY_ALLOCATED" })
      .then(r => setReceipts(r.data?.receipts || r.data?.items || []))
      .catch(() => {
        arReceiptApi.list({ customer_id: plan.customer_id })
          .then(r => setReceipts((r.data?.items || []).filter(r => ["VERIFIED", "PARTIALLY_ALLOCATED"].includes(r.status))))
          .catch(() => {});
      });
  }, [plan.customer_id]);

  const handleLink = async () => {
    if (!selectedId) { toast.error("Select a receipt"); return; }
    setSaving(true);
    try {
      const data = { receipt_id: selectedId };
      if (amount) data.amount_minor = Math.round(Number(amount) * 100);
      await arPayPlanApi.linkReceipt(plan.id, inst.id, data);
      toast.success("Receipt linked to installment");
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatError(e) || "Failed to link receipt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 12, maxWidth: 500, width: "100%", padding: 24 }}>
        <h3 style={{ fontWeight: 700, fontSize: 16, margin: "0 0 12px" }}>Link Receipt to Installment</h3>
        <p style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>
          Installment: {inst.milestone_label} — Outstanding: {fmt(inst.scheduled_amount_minor - (inst.applied_amount_minor || 0))}
        </p>
        {receipts.length === 0 ? (
          <p style={{ fontSize: 13, color: "#94A3B8", textAlign: "center", padding: "20px 0" }}>
            No verified receipts found for this customer.
          </p>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Select Receipt</label>
              <select value={selectedId} onChange={e => setSelected(e.target.value)}
                data-testid="link-receipt-select"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }}>
                <option value="">— Choose —</option>
                {receipts.map(r => (
                  <option key={r.id || r._id} value={r.id || r._id}>
                    {r.receipt_number} — {fmt(r.confirmed_amount_minor)} — {r.status}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
                Link Amount (₹) — leave blank to use full receipt amount
              </label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="Partial link amount"
                data-testid="link-receipt-amount"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1",
                  borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
            </div>
          </>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose}
            style={{ padding: "8px 18px", border: "1px solid #CBD5E1", borderRadius: 8,
              background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={handleLink} disabled={saving || !selectedId}
            data-testid="link-receipt-confirm-btn"
            style={{ padding: "8px 18px", border: "none", borderRadius: 8,
              background: saving || !selectedId ? "#CBD5E1" : "#1E293B", color: "#fff",
              fontSize: 13, fontWeight: 600, cursor: saving || !selectedId ? "default" : "pointer" }}>
            {saving ? "Linking…" : "Link Receipt"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Installment Row ───────────────────────────────────────────────────────────
function InstallmentRow({ plan, inst, onRefresh, canManage }) {
  const [expanded, setExpanded] = useState(false);
  const [modal, setModal] = useState(null); // "raise-invoice" | "link-receipt" | "waive" | "reminder"
  const [busy, setBusy] = useState(false);

  const outstanding = (inst.scheduled_amount_minor || 0) - (inst.applied_amount_minor || 0);
  const cfg = INST_CFG[inst.status] || INST_CFG.SCHEDULED;

  const handleRaiseInvoice = async () => {
    setBusy(true);
    try {
      const res = await arPayPlanApi.raiseInvoice(plan.id, inst.id, {});
      toast.success(`Invoice ${res.data.invoice_number} raised`);
      onRefresh();
    } catch (e) {
      toast.error(formatError(e) || "Failed to raise invoice");
    } finally {
      setBusy(false); setModal(null);
    }
  };

  const handleWaive = async (reason) => {
    setBusy(true);
    try {
      await arPayPlanApi.waiveInstallment(plan.id, inst.id, { reason });
      toast.success("Installment waived");
      onRefresh();
    } catch (e) {
      toast.error(formatError(e) || "Failed to waive installment");
    } finally {
      setBusy(false); setModal(null);
    }
  };

  const handleReminder = async () => {
    setBusy(true);
    try {
      const res = await arPayPlanApi.sendReminder(plan.id, { installment_id: inst.id, reminder_type: "MANUAL" });
      toast.success(`Mock reminder queued (job_id: ${res.data.job_ids?.[0]?.slice(0,8)}…)`);
      onRefresh();
    } catch (e) {
      toast.error(formatError(e) || "Failed to queue reminder");
    } finally {
      setBusy(false);
    }
  };

  const actionBtnStyle = (color = "#1E293B", disabled = false) => ({
    display: "flex", alignItems: "center", gap: 4, padding: "5px 10px",
    border: `1px solid ${disabled ? "#E2E8F0" : color}30`, borderRadius: 6,
    background: disabled ? "#F9FAFB" : `${color}0F`, color: disabled ? "#9CA3AF" : color,
    fontSize: 11, fontWeight: 600, cursor: disabled ? "default" : "pointer",
    transition: "opacity 0.2s",
  });

  return (
    <>
      <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, marginBottom: 8,
        background: "#fff", overflow: "hidden" }}>
        <div onClick={() => setExpanded(e => !e)} style={{ padding: "14px 16px", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 14 }}>
          {/* Number circle */}
          <div style={{ width: 32, height: 32, borderRadius: "50%",
            background: cfg.bg, color: cfg.color, display: "flex", alignItems: "center",
            justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            {inst.installment_number}
          </div>

          {/* Label + type */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#0F172A" }}>
              {inst.milestone_label}
            </div>
            <div style={{ fontSize: 11, color: "#94A3B8" }}>{inst.milestone_type}</div>
          </div>

          {/* Progress + amounts */}
          <div style={{ width: 120, flexShrink: 0 }}>
            <ProgressBar applied={inst.applied_amount_minor} scheduled={inst.scheduled_amount_minor} />
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 2, textAlign: "right" }}>
              {fmt(inst.applied_amount_minor)} / {fmt(inst.scheduled_amount_minor)}
            </div>
          </div>

          {/* Due date */}
          <div style={{ width: 90, textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: inst.status === "OVERDUE" ? "#DC2626" : "#374151" }}>
              {fmtDate(inst.due_date)}
            </div>
            <div style={{ fontSize: 10, color: "#94A3B8" }}>due date</div>
          </div>

          {/* Status badge */}
          <div style={{ flexShrink: 0 }}>
            <Badge status={inst.status} cfg={INST_CFG} />
          </div>

          {/* Expand toggle */}
          {expanded ? <ChevronUp size={14} color="#94A3B8" /> : <ChevronDown size={14} color="#94A3B8" />}
        </div>

        {/* Expanded actions */}
        {expanded && canManage && !["PAID", "WAIVED", "CANCELLED"].includes(inst.status) && (
          <div style={{ borderTop: "1px solid #F1F5F9", padding: "12px 16px",
            background: "#FAFAFA", display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!inst.invoice_id && (
              <button onClick={handleRaiseInvoice} disabled={busy}
                data-testid={`raise-invoice-${inst.id}`}
                style={actionBtnStyle("#1D4ED8", busy)}>
                <FileText size={11} /> Raise Invoice
              </button>
            )}
            {inst.invoice_id && (
              <span style={{ fontSize: 11, color: "#1D4ED8", padding: "5px 10px",
                background: "#EFF6FF", borderRadius: 6, fontWeight: 600 }}>
                Invoice: {inst.invoice_id.slice(0, 8)}…
              </span>
            )}
            <button onClick={() => setModal("link-receipt")} disabled={busy}
              data-testid={`link-receipt-${inst.id}`}
              style={actionBtnStyle("#059669", busy)}>
              <CreditCard size={11} /> Link Receipt
            </button>
            <button onClick={handleReminder} disabled={busy}
              data-testid={`send-reminder-${inst.id}`}
              style={actionBtnStyle("#7C3AED", busy)}>
              <Bell size={11} /> Mock Reminder
            </button>
            <button onClick={() => setModal("waive")} disabled={busy}
              data-testid={`waive-${inst.id}`}
              style={actionBtnStyle("#DC2626", busy || (inst.applied_amount_minor || 0) > 0)}>
              <Ban size={11} /> Waive
            </button>
            {outstanding > 0 && (
              <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "#DC2626",
                alignSelf: "center" }}>
                Outstanding: {fmt(outstanding)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal === "waive" && (
        <ConfirmModal
          title="Waive Installment"
          message={`Waive "${inst.milestone_label}" (${fmt(inst.scheduled_amount_minor)})? This is a Finance decision and requires a reason.`}
          confirmLabel="Waive Installment"
          confirmColor="#DC2626"
          field="reason"
          onClose={() => setModal(null)}
          onConfirm={handleWaive}
        />
      )}
      {modal === "raise-invoice" && (
        <ConfirmModal
          title="Raise Invoice"
          message={`Raise a DRAFT invoice for "${inst.milestone_label}" (${fmt(inst.scheduled_amount_minor)})?`}
          confirmLabel="Raise Invoice"
          confirmColor="#1D4ED8"
          onClose={() => setModal(null)}
          onConfirm={handleRaiseInvoice}
        />
      )}
      {modal === "link-receipt" && (
        <LinkReceiptModal plan={plan} inst={inst} onClose={() => setModal(null)} onDone={onRefresh} />
      )}
    </>
  );
}

// ── Main Detail Page ──────────────────────────────────────────────────────────
export default function ARPaymentPlanDetail() {
  const { planId }   = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const [plan, setPlan]         = useState(null);
  const [reminders, setReminders] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState("installments");
  const [modal, setModal]       = useState(null);
  const [busy, setBusy]         = useState(false);

  const canManage = user?.capabilities?.includes("ar.plan.manage");

  const loadPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await arPayPlanApi.get(planId);
      setPlan(res.data);
    } catch (e) {
      toast.error(formatError(e) || "Failed to load plan");
    } finally {
      setLoading(false);
    }
  }, [planId]);

  const loadReminders = useCallback(async () => {
    try {
      const res = await arPayPlanApi.getReminders(planId);
      setReminders(res.data);
    } catch (e) {
      toast.error(formatError(e) || "Failed to load reminders");
    }
  }, [planId]);

  useEffect(() => { loadPlan(); }, [loadPlan]);
  useEffect(() => {
    if (tab === "reminders") loadReminders();
  }, [tab, loadReminders]);

  const handleActivate = async () => {
    setBusy(true);
    try {
      await arPayPlanApi.activate(planId);
      toast.success("Payment plan activated");
      loadPlan();
    } catch (e) {
      toast.error(formatError(e) || "Failed to activate plan");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async (reason) => {
    setBusy(true);
    try {
      await arPayPlanApi.cancel(planId, { reason });
      toast.success("Payment plan cancelled");
      loadPlan();
      setModal(null);
    } catch (e) {
      toast.error(formatError(e) || "Failed to cancel plan");
    } finally {
      setBusy(false);
    }
  };

  const handleSendAllReminders = async () => {
    setBusy(true);
    try {
      const res = await arPayPlanApi.sendReminder(planId, { reminder_type: "MANUAL" });
      toast.success(`${res.data.scheduled_count} mock reminder(s) queued`);
      loadReminders();
    } catch (e) {
      toast.error(formatError(e) || "Failed to queue reminders");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div style={{ textAlign: "center", color: "#94A3B8" }}>
        <RefreshCw size={24} style={{ animation: "spin 1s linear infinite" }} />
        <p style={{ marginTop: 12 }}>Loading payment plan…</p>
      </div>
    </div>
  );

  if (!plan) return (
    <div style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>Plan not found.</div>
  );

  const planCfg = PLAN_CFG[plan.status] || PLAN_CFG.DRAFT;
  const insts   = plan.installments || [];
  const outstanding = plan.total_outstanding_amount_minor ?? (plan.total_scheduled_amount_minor - plan.total_applied_amount_minor);

  const TABS = [
    { id: "installments", label: "Installments", Icon: CalendarRange2 },
    { id: "reminders",    label: "Reminder History", Icon: Bell },
    { id: "audit",        label: "Audit Trail", Icon: History },
  ];

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Back + Actions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <button onClick={() => navigate("/ar/payment-plans")} data-testid="back-to-plans"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
            cursor: "pointer", fontSize: 13, color: "#64748B", fontWeight: 600 }}>
          <ArrowLeft size={14} /> Payment Plans
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          {canManage && plan.status === "DRAFT" && (
            <button onClick={handleActivate} disabled={busy} data-testid="activate-plan-btn"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
                border: "none", borderRadius: 8, background: "#1E293B", color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer" }}>
              <Play size={12} /> Activate Plan
            </button>
          )}
          {canManage && plan.status === "ACTIVE" && (
            <button onClick={handleSendAllReminders} disabled={busy} data-testid="send-all-reminders-btn"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
                border: "1px solid #7C3AED30", borderRadius: 8, background: "#F5F3FF", color: "#7C3AED",
                fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer" }}>
              <Bell size={12} /> Mock Reminders (All)
            </button>
          )}
          {canManage && ["DRAFT", "ACTIVE"].includes(plan.status) && (
            <button onClick={() => setModal("cancel")} data-testid="cancel-plan-btn"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
                border: "1px solid #FECACA", borderRadius: 8, background: "#FEF2F2", color: "#DC2626",
                fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Cancel Plan
            </button>
          )}
        </div>
      </div>

      {/* Plan Header */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", margin: 0 }}>{plan.plan_name}</h1>
              <span style={{ background: planCfg.bg, color: planCfg.color,
                border: `1px solid ${planCfg.color}30`, borderRadius: 9999,
                padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>{planCfg.label}</span>
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#64748B" }}>
              <span>{plan.plan_number}</span>
              <span>Customer: {plan.customer_id?.slice(0, 16)}…</span>
              <span>Booking: {plan.booking_id?.slice(0, 16)}…</span>
              <span>Created: {fmtDate(plan.created_at)}</span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#0F172A" }}>
              {fmt(plan.total_scheduled_amount_minor)}
            </div>
            <div style={{ fontSize: 11, color: "#94A3B8" }}>total scheduled</div>
          </div>
        </div>

        {/* Financial progress bar */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11,
            color: "#64748B", marginBottom: 4 }}>
            <span>Collected: {fmt(plan.total_applied_amount_minor)}</span>
            <span style={{ color: outstanding > 0 ? "#DC2626" : "#166534" }}>
              Outstanding: {fmt(outstanding)}
            </span>
          </div>
          <ProgressBar applied={plan.total_applied_amount_minor} scheduled={plan.total_scheduled_amount_minor} />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #E2E8F0", marginBottom: 16 }}>
        {[
          { id: "installments", label: "Installments", icon: "📅" },
          { id: "reminders",    label: "Reminder History", icon: "🔔" },
          { id: "audit",        label: "Audit Trail", icon: "🕐" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            style={{ padding: "8px 16px", border: "none", background: "none", cursor: "pointer",
              fontSize: 13, fontWeight: 600, color: tab === t.id ? "#1E293B" : "#64748B",
              borderBottom: tab === t.id ? "2px solid #1E293B" : "2px solid transparent",
              marginBottom: -1 }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Installments */}
      {tab === "installments" && (
        <div>
          {insts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#94A3B8" }}>
              No installments in this plan.
            </div>
          ) : (
            insts.map(inst => (
              <InstallmentRow
                key={inst.id}
                plan={plan}
                inst={inst}
                onRefresh={loadPlan}
                canManage={canManage && plan.status === "ACTIVE"}
              />
            ))
          )}
        </div>
      )}

      {/* Tab: Reminders */}
      {tab === "reminders" && (
        <div>
          {!reminders ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#94A3B8" }}>Loading…</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
                    {reminders.scheduled_jobs?.length || 0} queued jobs
                  </span>
                  <span style={{ fontSize: 12, color: "#64748B", marginLeft: 8 }}>
                    {reminders.delivered_reminders?.length || 0} delivered (MOCK)
                  </span>
                </div>
                <div style={{ padding: "3px 10px", background: "#F5F3FF", border: "1px solid #C4B5FD",
                  borderRadius: 9999, fontSize: 11, fontWeight: 600, color: "#7C3AED" }}>
                  UAT MOCK MODE — No real messages sent
                </div>
              </div>

              {/* Queued jobs */}
              {reminders.scheduled_jobs?.map(job => (
                <div key={job.id} style={{ border: "1px solid #E2E8F0", borderRadius: 8,
                  padding: "12px 14px", marginBottom: 8, background: "#FAFAFA" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>
                        {job.payload?.reminder_type || "MANUAL"} reminder
                      </span>
                      <span style={{ fontSize: 11, color: "#64748B", marginLeft: 8 }}>
                        Installment: {job.payload?.installment_id?.slice(0, 8)}…
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{
                        padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 600,
                        background: job.status === "COMPLETED" ? "#F0FDF4" : job.status === "FAILED" ? "#FEF2F2" : "#FEF3C7",
                        color: job.status === "COMPLETED" ? "#166534" : job.status === "FAILED" ? "#991B1B" : "#92400E",
                      }}>{job.status}</span>
                      <span style={{ fontSize: 11, color: "#94A3B8" }}>{fmtDatetime(job.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Delivered audit records */}
              {reminders.delivered_reminders?.map(r => (
                <div key={r.id} style={{ border: "1px solid #E2E8F0", borderRadius: 8,
                  padding: "12px 14px", marginBottom: 8, background: "#F5F3FF" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#7C3AED" }}>
                        [MOCK] {r.reminder_type}
                      </span>
                      <span style={{ fontSize: 11, color: "#64748B", marginLeft: 8 }}>
                        Outstanding: {fmt(r.outstanding_minor)}
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>{fmtDatetime(r.delivered_at)}</span>
                  </div>
                </div>
              ))}

              {(!reminders.scheduled_jobs?.length && !reminders.delivered_reminders?.length) && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#94A3B8" }}>
                  No reminders have been sent for this plan.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Tab: Audit Trail */}
      {tab === "audit" && (
        <div>
          {(plan.audit_trail || []).map((entry, idx) => (
            <div key={idx} style={{ border: "1px solid #E2E8F0", borderRadius: 8,
              padding: "12px 14px", marginBottom: 8, background: "#FAFAFA",
              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: 13, color: "#0F172A" }}>{entry.action}</span>
                {entry.note && <span style={{ fontSize: 12, color: "#64748B", marginLeft: 8 }}>{entry.note}</span>}
              </div>
              <div style={{ fontSize: 11, color: "#94A3B8", textAlign: "right" }}>
                <div>{entry.by}</div>
                <div>{fmtDatetime(entry.at)}</div>
              </div>
            </div>
          ))}
          {(!plan.audit_trail || plan.audit_trail.length === 0) && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#94A3B8" }}>
              No audit entries.
            </div>
          )}
        </div>
      )}

      {/* Cancel modal */}
      {modal === "cancel" && (
        <ConfirmModal
          title="Cancel Payment Plan"
          message="Cancelling the plan will void all SCHEDULED/DUE installments. Paid installments cannot be cancelled."
          confirmLabel="Cancel Plan"
          confirmColor="#DC2626"
          field="reason"
          fieldLabel="Cancellation Reason"
          onClose={() => setModal(null)}
          onConfirm={handleCancel}
        />
      )}
    </div>
  );
}

// Tiny workaround for missing Lucide import
function CalendarRange2(props) {
  return (
    <svg width={props.size || 16} height={props.size || 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
}
